import { ClerkProvider } from '@clerk/clerk-react';
import { PostHogProvider } from 'posthog-js/react';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

// fonts
import '@fontsource-variable/inter';
import '@fontsource-variable/quicksand';

import App from './App';
import { AppConfig } from './services/app-config-service';

// Suppress Clerk development warning and popup errors
if (import.meta.env.DEV) {
  const originalWarn = console.warn;
  const originalError = console.error;

  console.warn = (...args) => {
    if (args[0]?.includes?.('Clerk has been loaded with development keys')) {
      return;
    }
    originalWarn.apply(console, args);
  };

  // Suppress Clerk popup.js errors (non-critical internal errors)
  console.error = (...args) => {
    const errorMessage = args[0]?.toString() || '';
    if (
      errorMessage.includes('popup.js') &&
      errorMessage.includes("Cannot read properties of undefined (reading 'enabled')")
    ) {
      // Suppress this non-critical Clerk internal error
      return;
    }
    originalError.apply(console, args);
  };
}

// Handle unhandled promise rejections from Clerk popup.js
window.addEventListener('unhandledrejection', (event) => {
  const errorMessage = event.reason?.toString() || '';
  if (
    errorMessage.includes('popup.js') &&
    errorMessage.includes("Cannot read properties of undefined (reading 'enabled')")
  ) {
    // Prevent this non-critical Clerk internal error from breaking the app
    event.preventDefault();
  }
});

// Let's make sure the required env vars exist before going forward
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}

// Only enable PostHog in production (bonds.avodah.dev)
const posthogApiKey = AppConfig.isProduction ? import.meta.env.VITE_PUBLIC_POSTHOG_KEY : undefined;

// Wrapper to conditionally include PostHog provider (avoids warnings in non-production)
const AppWithProviders = () => {
  const clerkApp = (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      appearance={{
        variables: { colorPrimary: '#1E918E' },
        elements: {
          formButtonPrimary: 'bg-slate-500 hover:bg-slate-400 text-sm normal-case',
        },
      }}
    >
      <App />
    </ClerkProvider>
  );

  // Only wrap with PostHog in production
  if (posthogApiKey) {
    return (
      <PostHogProvider
        apiKey={posthogApiKey}
        options={{
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
          defaults: '2025-05-24',
          capture_exceptions: true,
        }}
      >
        {clerkApp}
      </PostHogProvider>
    );
  }

  return clerkApp;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <AppWithProviders />
  </React.StrictMode>
);
